import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import Icon from './Icon';
import TaskComments from './TaskComments';
import { Avatar, Pop, dueInfo, CATEGORY_OPTIONS } from './TaskList';

// Görev detay paneli (sağdan açılır). Her alan yerinde düzenlenir ve anında kaydedilir.

function TextField({ label, value, onSave, rows = 2, placeholder }) {
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  return (
    <div className="tp-field">
      <div className="tp-label">{label}</div>
      <textarea className="form-input tp-text" rows={rows} value={v} placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if ((v || '') !== (value || '')) onSave(v); }} />
    </div>
  );
}

function Checklist({ taskId, onCounts }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const ref = useRef(null);
  const apply = (list) => { setItems(list); onCounts(list.length, list.filter(i => i.done).length); };

  useEffect(() => {
    api.get(`/tasks/${taskId}/checklist`).then(r => setItems(r.data)).catch(() => {});
  }, [taskId]);

  const add = async () => {
    const body = text.trim();
    if (!body) return;
    try { apply((await api.post(`/tasks/${taskId}/checklist`, { body })).data); setText(''); } catch (e) { /* toast */ }
    if (ref.current) ref.current.focus();
  };
  const toggle = async (it) => {
    setItems(list => list.map(x => (x.id === it.id ? { ...x, done: !it.done } : x)));
    try { apply((await api.put(`/tasks/${taskId}/checklist/${it.id}`, { done: !it.done })).data); } catch (e) { /* toast */ }
  };
  const rename = async (it) => {
    const body = editText.trim(); setEditId(null);
    if (!body || body === it.body) return;
    try { apply((await api.put(`/tasks/${taskId}/checklist/${it.id}`, { body })).data); } catch (e) { /* toast */ }
  };
  const remove = async (it) => {
    try { apply((await api.delete(`/tasks/${taskId}/checklist/${it.id}`)).data); } catch (e) { /* toast */ }
  };

  const done = items.filter(i => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  return (
    <div className="tp-section">
      <div className="tp-section__head">
        <span>Kontrol listesi</span>
        {items.length > 0 && <span className="text-muted">{done}/{items.length}</span>}
      </div>
      {items.length > 0 && <div className="tp-progress"><span style={{ width: `${pct}%` }} /></div>}
      {items.map(it => (
        <div key={it.id} className={`tp-check ${it.done ? 'is-done' : ''}`}>
          <button type="button" className="tp-check__box" onClick={() => toggle(it)} title={it.done ? `${it.done_by_name || ''} işaretledi` : 'İşaretle'}>
            <Icon name={it.done ? 'squareCheck' : 'square'} size={16} />
          </button>
          {editId === it.id ? (
            <input className="tp-check__input" autoFocus value={editText} maxLength={500}
              onChange={(e) => setEditText(e.target.value)} onBlur={() => rename(it)}
              onKeyDown={(e) => { if (e.key === 'Enter') rename(it); if (e.key === 'Escape') setEditId(null); }} />
          ) : (
            <span className="tp-check__text" onClick={() => { setEditId(it.id); setEditText(it.body); }}>{it.body}</span>
          )}
          <button type="button" className="tp-check__del" title="Maddeyi sil" onClick={() => remove(it)}><Icon name="x" size={11} /></button>
        </div>
      ))}
      <div className="tp-check tp-check--new">
        <span className="tp-check__box tp-ghost"><Icon name="plus" size={14} /></span>
        <input ref={ref} className="tp-check__input" value={text} maxLength={500} placeholder="Madde ekle, Enter ile kaydet"
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
      </div>
    </div>
  );
}

export default function TaskPanel({ task, users, accounts, statuses, statusMap, priorityMap, canDelete, onUpdate, onDelete, onClose, onPlay, onLocalChange }) {
  const [title, setTitle] = useState(task.title || '');
  const [menu, setMenu] = useState(null);
  useEffect(() => setTitle(task.title || ''), [task.id, task.title]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !menu) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu, onClose]);

  const save = (patch) => onUpdate(task.id, patch);
  const st = statusMap[task.status] || statusMap.todo;
  const people = Array.isArray(task.assignees) ? task.assignees : [];
  const due = dueInfo(task.due_date);
  const fmt = (d) => (d ? new Date(d).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  const toggleAssignee = (uid) => {
    const ids = people.map(p => p.id);
    save({ assignee_ids: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid] });
  };
  const saveTitle = () => {
    const v = title.trim();
    if (!v) { setTitle(task.title); return; }
    if (v !== task.title) save({ title: v });
  };

  // body'ye çizilir: sayfa kapsayıcısındaki animasyon/transform sabit konumu bozmasın
  return createPortal(
    <>
      <div className="drawer-overlay tp-overlay" onClick={onClose} />
      <aside className="drawer tp" role="dialog" aria-label="Görev detayı">
        <div className="tp-top">
          <span style={{ position: 'relative' }}>
            <button type="button" className="tl-pill tp-status" style={{ background: st.color, color: '#0E0F11' }} onClick={() => setMenu('status')}>
              {st.label} <Icon name="chevronDown" size={12} />
            </button>
            <Pop open={menu === 'status'} onClose={() => setMenu(null)}>
              {statuses.map(([k, v]) => (
                <button key={k} type="button" className={`tl-pop__item ${k === task.status ? 'is-on' : ''}`}
                  onClick={() => { setMenu(null); if (k !== task.status) save({ status: k }); }}>
                  <span className="tl-sq" style={{ background: v.color }} />{v.label}
                </button>
              ))}
            </Pop>
          </span>
          {task.status !== 'done' && (
            <button type="button" className="btn btn--secondary tp-done" onClick={() => save({ status: 'done' })}>
              <Icon name="check" size={13} /> Tamamla
            </button>
          )}
          <button type="button" className="drawer__close" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Kapat">&times;</button>
        </div>

        <div className="drawer__body tp-body">
          <textarea className="tp-title" rows={1} value={title} maxLength={200}
            onChange={(e) => setTitle(e.target.value.replace(/\n/g, ' '))} onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }} />

          <div className="tp-props">
            <span className="tp-k"><Icon name="building" size={13} /> Marka</span>
            <span>
              <select className="tp-select" value={task.account_id || ''} onChange={(e) => save({ account_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Markasız (iç iş)</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                {task.account_id && !accounts.some(a => a.id === task.account_id) && <option value={task.account_id}>{task.account_name}</option>}
              </select>
              {!task.account_id && task.company && <span className="text-muted" style={{ fontSize: 11.5, marginLeft: 6 }}>(eski firma: {task.company})</span>}
            </span>

            <span className="tp-k"><Icon name="users" size={13} /> Atanan</span>
            <span style={{ position: 'relative' }}>
              <button type="button" className="tp-people" onClick={() => setMenu('assignee')}>
                {people.length === 0 && <span className="text-muted">Kişi ata</span>}
                {people.map(p => <span key={p.id} className="tp-chip"><Avatar user={p} size={18} />{p.name}</span>)}
                <span className="tp-ghost"><Icon name="plus" size={12} /></span>
              </button>
              <Pop open={menu === 'assignee'} onClose={() => setMenu(null)} width={250}>
                <div className="tl-pop__head">Atanan kişiler</div>
                <div className="tl-pop__scroll">
                  {users.map(u => {
                    const on = people.some(p => p.id === u.id);
                    return (
                      <button key={u.id} type="button" className={`tl-pop__item ${on ? 'is-on' : ''}`} onClick={() => toggleAssignee(u.id)}>
                        <Avatar user={u} size={18} /> <span style={{ flex: 1 }}>{u.name}</span>{on && <Icon name="check" size={12} />}
                      </button>
                    );
                  })}
                </div>
              </Pop>
            </span>

            <span className="tp-k"><Icon name="calendar" size={13} /> Bitiş</span>
            <span className="tp-inline">
              <input key={task.due_date || 'none'} type="date" className="tp-select" defaultValue={task.due_date ? String(task.due_date).slice(0, 10) : ''}
                onBlur={(e) => {
                  const v = e.target.value; const cur = task.due_date ? String(task.due_date).slice(0, 10) : '';
                  if (v !== cur && (!v || v >= '2000-01-01')) save({ due_date: v || null });
                }}
                onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
              {due && task.status !== 'done' && <span style={{ color: due.color, fontSize: 12 }}>{due.label}</span>}
              {task.due_date && <button type="button" className="tl-x" style={{ opacity: 1 }} title="Tarihi kaldır" onClick={() => save({ due_date: null })}><Icon name="x" size={10} /></button>}
            </span>

            <span className="tp-k"><Icon name="list" size={13} /> Kategori</span>
            <span>
              <select className="tp-select" value={task.category || ''} onChange={(e) => save({ category: e.target.value || null })}>
                <option value="">Kategorisiz</option>
                {CATEGORY_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </span>

            <span className="tp-k"><Icon name="flag" size={13} /> Öncelik</span>
            <span className="tp-inline">
              {Object.entries(priorityMap).map(([k, v]) => (
                <button key={k} type="button" className={`tp-prio ${task.priority === k ? 'is-on' : ''}`}
                  style={task.priority === k ? { borderColor: v.color, color: v.color } : null} onClick={() => task.priority !== k && save({ priority: k })}>
                  <Icon name="flag" size={12} /> {v.label}
                </button>
              ))}
            </span>

            <span className="tp-k"><Icon name="alert" size={13} /> Onay</span>
            <label className="tp-inline" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={!!task.approval_required} onChange={(e) => save({ approval_required: e.target.checked })} />
              <span className={task.approval_required ? '' : 'text-muted'}>Müşteri / yönetici onayı gerekli</span>
            </label>

            {task.source_meeting && (<>
              <span className="tp-k"><Icon name="play" size={12} /> Kaynak</span>
              <span>
                <button type="button" className="tl-src" style={{ fontSize: 12 }}
                  onClick={() => onPlay(task.source_meeting.meeting_id, task.source_meeting.ts_seconds)}>
                  <Icon name="play" size={10} /> {task.source_meeting.title || 'Toplantı'}
                </button>
              </span>
            </>)}
          </div>

          <TextField label="Açıklama" value={task.description} rows={5} placeholder="Detay, bağlantı, not…" onSave={(v) => save({ description: v })} />
          <div className="tp-two">
            <TextField label="Yapılan son işlem" value={task.last_action} onSave={(v) => save({ last_action: v })} />
            <TextField label="Sonraki adım" value={task.next_step} onSave={(v) => save({ next_step: v })} />
          </div>

          <Checklist taskId={task.id} onCounts={(total, done) => onLocalChange(task.id, { checklist_total: total, checklist_done: done })} />

          <div className="tp-section">
            <TaskComments taskId={task.id} users={users} onCountChange={(n) => onLocalChange(task.id, { comment_count: n })} />
          </div>

          <div className="tp-foot">
            <span>Oluşturan: {task.created_by_name || (task.source_meeting ? 'Fireflies (otomatik, toplantı maddesinden)' : '—')} · {fmt(task.created_at)}</span>
            <span>Son güncelleme: {fmt(task.updated_at)}</span>
            {canDelete && <button type="button" className="tp-del" onClick={() => onDelete(task.id)}><Icon name="trash" size={12} /> Görevi sil</button>}
          </div>
        </div>
      </aside>
    </>,
    document.body
  );
}
